// Buying a pack on the phone (C3, D2; docs/PLAY_PRODUCTS.md). In the Play app it goes through Play
// Billing (the Digital Goods and Payment Request APIs); anywhere else through Stripe Checkout, which
// comes back to /map?…&session_id=…. Either way the server confirms the purchase with the store
// (POST /api/billing/confirm) before the pack is the family's. The PIN comes first (screens/UnlockSheet).
import { formatPrice, type CatalogueEntry } from '../domain/packs'
import { api } from './api'
import { usePacks } from './packs'

const PLAY_BILLING = 'https://play.google.com/billing'

interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<{ itemId: string; price: { currency: string; value: string } }[]>
  listPurchases(): Promise<{ itemId: string; purchaseToken: string }[]>
}
type WithGoods = Window & { getDigitalGoodsService?: (method: string) => Promise<DigitalGoodsService> }

/** Play Billing, when this is the Play app and the server can confirm Play purchases. */
async function playService(): Promise<DigitalGoodsService | null> {
  const w = window as WithGoods
  if (!usePacks.getState().play || typeof w.getDigitalGoodsService !== 'function') return null
  return w.getDigitalGoodsService(PLAY_BILLING).catch(() => null)
}

/** Play's own price for the pack (in the Play app), formatted for this phone; null elsewhere. */
export async function playPrice(pack: CatalogueEntry): Promise<string | null> {
  const goods = await playService()
  const item = (await goods?.getDetails([pack.sku]).catch(() => []))?.[0]
  return item ? formatPrice(Math.round(Number(item.price.value) * 100), item.price.currency, navigator.language) : null
}

export type BuyResult = 'bought' | 'leaving' | 'cancelled' | 'off' | 'failed'

async function confirm(report: { provider: 'stripe'; sessionId: string } | { provider: 'play'; sku: string; purchaseToken: string }): Promise<boolean> {
  const res = await api<{ packs: string[] }>('/api/billing/confirm', { method: 'POST', body: JSON.stringify(report) }).catch(() => null)
  if (res) usePacks.getState().set({ owned: [...res.packs].sort() })
  return !!res
}

export async function buy(pack: CatalogueEntry): Promise<BuyResult> {
  const goods = await playService()
  if (goods) {
    const request = new PaymentRequest([{ supportedMethods: PLAY_BILLING, data: { sku: pack.sku } }], { total: { label: pack.name, amount: { currency: 'AUD', value: '0' } } })
    const response = await request.show().catch(() => null)
    if (!response) return 'cancelled'
    const token = (response.details as { purchaseToken?: string } | null)?.purchaseToken
    const ok = !!token && (await confirm({ provider: 'play', sku: pack.sku, purchaseToken: token }))
    await response.complete(ok ? 'success' : 'fail').catch(() => undefined)
    return ok ? 'bought' : 'failed'
  }
  if (!usePacks.getState().web) return 'off'
  const res = await api<{ url: string }>('/api/billing/checkout', { method: 'POST', body: JSON.stringify({ packId: pack.id, currency: currencyHint() }) }).catch(() => null)
  if (!res) return 'failed'
  location.assign(res.url)
  return 'leaving'
}

/** The phone's region's currency, for Stripe (it falls back to USD when the pack has no price in it). */
function currencyHint(): string | undefined {
  const region = navigator.language.split('-')[1]?.toUpperCase()
  return region === 'AU' ? 'AUD' : region === 'NZ' ? 'NZD' : region === 'GB' ? 'GBP' : region === 'US' ? 'USD' : undefined
}

/** A new phone (or a reinstall): Play's purchases for this Google account, confirmed again. */
export async function restore(): Promise<void> {
  const goods = await playService()
  for (const p of (await goods?.listPurchases().catch(() => [])) ?? []) await confirm({ provider: 'play', sku: p.itemId, purchaseToken: p.purchaseToken })
}

// Back from Stripe Checkout: its session id, taken before the router tidies the address.
let checkoutReturn: string | null =
  typeof location !== 'undefined' && /^cs_[A-Za-z0-9_]+$/.test(new URLSearchParams(location.search).get('session_id') ?? '')
    ? new URLSearchParams(location.search).get('session_id')
    : null

/** Confirms a Checkout the phone just came back from (once). True when a pack was added. */
export async function confirmCheckoutReturn(): Promise<boolean> {
  const id = checkoutReturn
  checkoutReturn = null
  return id ? confirm({ provider: 'stripe', sessionId: id }) : false
}
